import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import PackagesTemplate from "templates/events/packages";
import PackageServicesForm from "templates/events/packages/servicesForm";
import { PACKAGE_LEVELS } from "templates/events/packages/structure";
import {
  activatePackageRequest,
  createPackageRequest,
  deactivatePackageRequest,
  deletePackageRequest,
  getPackageDetailsRequest,
  getPackagesRequest,
  updatePackageRequest,
} from "services/modules/events/packages";
import { getScrollEventTypesRequest } from "services/modules/events/eventTypes";

function PackagesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.packages.packages);
  const count = useSelector((state) => state.packages.count);
  const details = useSelector((state) => state.packages.packageDetails);
  const load = useSelector((state) => state.packages.load);
  const filterEventTypes = useSelector(
    (state) => state.eventTypes.filterEventTypes,
  );

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions?.packages,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);
  const [packageItems, setPackageItems] = useState([]);
  const [selectedEventTypeId, setSelectedEventTypeId] = useState("");

  const eventTypesOptions =
    filterEventTypes?.map((et) => ({
      id: et.id,
      name:
        et?.localized_data?.name_en ||
        et?.localized_data?.en?.name ||
        et?.name_en ||
        et?.name ||
        `#${et.id}`,
    })) || [];

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => ({ ...prev, page_number: page }));
  };

  const getDetails = async () => {
    dispatch(getPackageDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    dispatch(
      getPackagesRequest({
        params: {
          page_size: 10,
          page_number: 1,
          keyword: "",
          sort_by: "id",
          ...filterObj,
        },
      }),
    );
  };

  const mappedData = list?.map((data) => {
    const nameEn =
      data?.localized_data?.name_en ||
      data?.localized_data?.en?.name ||
      data?.name_en ||
      data?.name ||
      "";
    return {
      ...data,
      name: nameEn,
      name_en: nameEn,
      event_type_name: data?.event_type?.name || "-",
      level_label: t(`events_packages.levels.${data?.level}`, {
        defaultValue: data?.level,
      }),
      base_price_label: `${data?.base_price ?? "-"} ${data?.currency || "SAR"}`,
      display_priority: data?.display_priority ?? "-",
      image_url: data?.cover_image ? (
        <img
          src={data.cover_image}
          alt={nameEn}
          width="125px"
          height="77px"
        />
      ) : (
        t("chat.no_image")
      ),
      is_active_label: (
        <>{data?.is_active ? t("common.yes") : t("common.no")}</>
      ),
    };
  });

  const rowData = () => {};

  const breadcrumbs = [
    { active: true, path: "/", title: t("common.home") },
    {
      active: !type ? false : true,
      path: "/events/packages",
      title: t("events_packages.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("events_packages.details")
          : id
            ? t("events_packages.edit")
            : t("events_packages.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/events/packages");
      getList(filterObj);
      !id && formik?.resetForm();
      setPackageItems([]);
      setSelectedEventTypeId("");
      toast.success(
        id ? t("events_packages.updated") : t("events_packages.created"),
      );
    };

    const rawImages = data?.images || formik?.values?.images || [];
    const images = (Array.isArray(rawImages) ? rawImages : [])
      .map((img) => (typeof img === "string" ? img : img?.url || img))
      .filter(Boolean);

    const items = (packageItems || []).map((item, index) => ({
      variant_id: Number(item.variant_id),
      kind: item.kind || "included",
      quantity: Number(item.quantity) || 1,
      display_priority:
        item.display_priority === "" || item.display_priority == null
          ? index
          : Number(item.display_priority),
    }));

    const requestData = {
      body: {
        package: {
          event_type_id: Number(formik?.values?.event_type_id),
          level: formik?.values?.level,
          name_en: formik?.values?.name_en,
          name_ar: formik?.values?.name_ar,
          description_en: formik?.values?.description_en,
          description_ar: formik?.values?.description_ar,
          cover_image: data?.cover_image || formik?.values?.cover_image,
          images,
          max_guests: formik?.values?.max_guests
            ? Number(formik.values.max_guests)
            : null,
          display_priority: formik?.values?.display_priority
            ? Number(formik.values.display_priority)
            : 0,
          is_active: !!formik?.values?.is_active,
          items,
        },
      },
      action,
    };

    if (id) {
      dispatch(updatePackageRequest({ ...requestData, id }));
    } else {
      dispatch(createPackageRequest(requestData));
    }
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("events_packages.event_type"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          event_type_id: value?.value,
          page_number: 1,
        }));
      },
      options: eventTypesOptions,
    },
    {
      type: "filter",
      placeholder: t("events_packages.level"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          level: value?.value,
          page_number: 1,
        }));
      },
      options: PACKAGE_LEVELS.map((l) => ({
        id: l.id,
        name: t(`events_packages.levels.${l.id}`, { defaultValue: l.name }),
      })),
    },
    {
      type: "filter",
      placeholder: t("events_packages.is_active"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          is_active: value?.value,
          page_number: 1,
        }));
      },
      options: [
        { id: true, name: t("common.yes") },
        { id: false, name: t("common.no") },
      ],
    },
  ];

  const handleDelete = (deleteId) => {
    const action = () => {
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({ ...filterObj, page_number: page - 1 });
        getList({ ...filterObj, page_number: page - 1 });
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/events/packages");
      toast.success(t("events_packages.deleted"));
    };
    dispatch(deletePackageRequest({ id: deleteId, action }));
    setOpenDeleteModal(false);
  };

  const handleToggleActive = (data) => {
    const isActive = data?.is_active === true;
    const action = () => {
      toast.success(
        isActive
          ? t("events_packages.deactivated")
          : t("events_packages.activated"),
      );
      getList(filterObj);
    };
    const requestData = { id: data?.id, action };
    if (isActive) {
      dispatch(deactivatePackageRequest(requestData));
    } else {
      dispatch(activatePackageRequest(requestData));
    }
  };

  const actionsList = ({ data }) => {
    const isActive = data?.is_active === true;
    return [
      {
        condition: () => true,
        click: () => navigate(`?type=details&id=${data?.id}`),
        text: t("common.details"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_update,
        click: () => navigate(`?type=form&id=${data?.id}`),
        text: t("common.edit"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_update,
        click: () => handleToggleActive(data),
        text: isActive
          ? t("events_packages.deactivate")
          : t("events_packages.activate"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_delete,
        click: () => setOpenDeleteModal(true),
        text: t("common.delete"),
      },
    ];
  };

  const handleEventTypeChange = (value) => {
    setSelectedEventTypeId(value || "");
  };

  useEffect(() => {
    dispatch(
      getScrollEventTypesRequest({
        params: { page_size: 100, page_number: 1, is_active: true },
      }),
    );
  }, []);

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  useEffect(() => {
    if (type === "form" && id && details?.event_type?.id) {
      setSelectedEventTypeId(details.event_type.id);
    }
    if (type === "form" && !id) {
      setSelectedEventTypeId("");
      setPackageItems([]);
    }
  }, [type, id, details?.event_type?.id]);

  const formChildren = (
    <PackageServicesForm
      eventTypeId={selectedEventTypeId}
      packageId={id || null}
      value={packageItems}
      onChange={setPackageItems}
    />
  );

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <PackagesTemplate
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={<></>}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={[]}
        can_create={isSuperAdmin || permissions?.can_create}
        can_update={isSuperAdmin || permissions?.can_update}
        eventTypesOptions={eventTypesOptions}
        formChildren={formChildren}
        onEventTypeChange={handleEventTypeChange}
      />
    </>
  );
}

export default PackagesPage;
